import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Typography,
  Slider,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  RadioGroup,
  Divider,
  Radio,
} from "@mui/material";
import { Navigate } from "react-router-dom";
import LoadingButton from "@mui/lab/LoadingButton";
import moment from "moment";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import CoachSubscriptionModal from "./CoachSubscriptionModal";
import { loadStripe } from "@stripe/stripe-js";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import { useAuth } from "../../utils/auth";
import { calculateTotalWithPaymentsPro } from "../../utils/slotPricing";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: "Roboto Mono, monospace",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#fa755a", iconColor: "#fa755a" },
  },
};

const CoachPaymentMethodsManager = ({
  paymentMethods,
  defaultPaymentMethodId,
  setDefaultPaymentMethodId,
  setPaymentMethods,
  stripeCustomerId,
  userEmail,
  setDefaultPaymentMethod,
  deletePaymentMethod,
}) => {
  const { createSetupIntent, getPaymentMethods } = useApi();
  const stripe = useStripe();
  const elements = useElements();
  const [adding, setAdding] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [error, setError] = useState("");

  const handleAddPaymentMethod = async () => {
    if (!stripe || !elements) return;

    setAdding(true);
    setError("");

    try {
      const card = elements.getElement(CardElement);
      if (!card) throw new Error("Card element not ready");

      const { client_secret: clientSecret } = await createSetupIntent({
        stripeCustomerId,
      });
      if (!clientSecret) throw new Error("Failed to create setup intent");

      const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card,
            billing_details: { email: userEmail },
          },
        }
      );
      if (setupError) throw new Error(setupError.message);

      const newPmId = setupIntent?.payment_method;
      if (!newPmId) throw new Error("Setup did not return a payment method");

      if (setAsDefault) {
        await setDefaultPaymentMethod({
          paymentMethodId: newPmId,
          stripeCustomerId,
        });
      }

      const pmResponse = await getPaymentMethods(stripeCustomerId);
      setPaymentMethods(pmResponse?.paymentMethods || []);
      setDefaultPaymentMethodId(pmResponse?.defaultPaymentMethodId);

      card.clear();
    } catch (err) {
      setError(err.message || "Failed to add payment method");
    } finally {
      setAdding(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    try {
      await setDefaultPaymentMethod({
        paymentMethodId,
        stripeCustomerId,
      });
      setDefaultPaymentMethodId(paymentMethodId);
    } catch (err) {
      setError(err.message || "Failed to update default payment method");
    }
  };

  const handleDelete = async (paymentMethodId) => {
    if (
      paymentMethodId === defaultPaymentMethodId ||
      paymentMethods.length <= 1
    ) {
      return;
    }
    try {
      await deletePaymentMethod({
        paymentMethodId,
      });
      setPaymentMethods((prev) =>
        prev.filter((pm) => pm.id !== paymentMethodId)
      );
    } catch (err) {
      setError(err.message || "Failed to delete payment method");
    }
  };

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography sx={{ mb: 2 }}>
        <b>Payment Methods</b>
      </Typography>
      {paymentMethods.length === 0 ? (
        <Typography sx={{ mb: 2 }}>
          No saved payment methods. Add one below.
        </Typography>
      ) : (
        <RadioGroup
          value={defaultPaymentMethodId || ""}
          onChange={(e) => handleSetDefault(e.target.value)}
        >
          {paymentMethods.map((pm) => {
            const isDefault = pm.id === defaultPaymentMethodId;
            const canDelete = paymentMethods.length > 1 && !isDefault;
            return (
              <Box
                key={pm.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 1,
                  mb: 1,
                }}
              >
                <FormControlLabel
                  value={pm.id}
                  control={<Radio />}
                  label={`${pm.card?.brand?.toUpperCase() || "CARD"} •••• ${
                    pm.card?.last4 || "****"
                  } Exp: ${pm.card?.exp_month || "**"}/${
                    pm.card?.exp_year || "**"
                  }${isDefault ? " (Default)" : ""}`}
                />
                <Button
                  size="small"
                  color="error"
                  disabled={!canDelete}
                  onClick={() => handleDelete(pm.id)}
                >
                  Delete
                </Button>
              </Box>
            );
          })}
        </RadioGroup>
      )}
      <Divider sx={{ my: 2 }} />
      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Add a new payment method
      </Typography>
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          p: 2,
          mb: 1,
        }}
      >
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={setAsDefault}
            onChange={() => setSetAsDefault((prev) => !prev)}
          />
        }
        label="Set as default"
      />
      <Box sx={{ mt: 1 }}>
        <LoadingButton
          variant="contained"
          onClick={handleAddPaymentMethod}
          loading={adding}
        >
          Add Payment Method
        </LoadingButton>
      </Box>
    </Paper>
  );
};

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const appearance = {
  theme: "stripe",
  variables: {
    colorPrimary: "#ff0000",
    fontFamily: "Roboto Mono, monospace",
  },
};

const CoachSubscription = () => {
  const { user } = useAuth();
  const { id, isCoach, stripeCustomerId, email } = useUserInfo();
  const {
    getCoachSubscription,
    updateCoachSubscription,
    cancelCoachSubscription,
    getPaymentMethods,
    setDefaultPaymentMethod,
    deletePaymentMethod,
  } = useApi();
  const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);

  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(false);
  const [slots, setSlots] = useState(10);
  const [paymentsPro, setPaymentsPro] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState();

  useEffect(() => {
    if (!loading && !sub?.hasSubscription) {
      setSubscribeModalOpen(true);
    }
  }, [loading, sub?.hasSubscription]);
  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await getCoachSubscription();
        if (res?.hasSubscription && res?.ok) {
          setSub(res);
          setSlots(res.slots || 10);
          setPaymentsPro(!!res.paymentsPro);
        } else {
          setSub(res);
        }
      } catch (err) {
        setError(err?.message || "Failed to load subscription");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [getCoachSubscription]);

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      if (!stripeCustomerId) return;
      try {
        const pmResponse = await getPaymentMethods(stripeCustomerId);
        setPaymentMethods(pmResponse?.paymentMethods || []);
        setDefaultPaymentMethodId(
          pmResponse?.defaultPaymentMethodId || undefined
        );
      } catch (err) {
        console.error("Failed to load payment methods:", err);
      }
    };
    fetchPaymentMethods();
  }, [stripeCustomerId, getPaymentMethods]);

  if (!user) return <Navigate to="/login" replace />;
  if (!isCoach) return <Navigate to="/clients" replace />;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!sub?.hasSubscription) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Subscription
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Subscribe to add client slots and optionally enable Payments Pro to
          collect payments from your clients.
        </Alert>
        <Button
          variant="contained"
          onClick={() => setSubscribeModalOpen(true)}
          sx={{ mt: 2 }}
        >
          Get Started
        </Button>
        <Elements
          stripe={stripePromise}
          options={{
            appearance,
            fonts: [
              {
                family: "Roboto Mono, monospace",
                cssSrc:
                  "https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@100;400;500;700",
              },
            ],
          }}
        >
          <CoachSubscriptionModal
            open={subscribeModalOpen}
            onClose={() => setSubscribeModalOpen(false)}
            existingStripeCustomerId={stripeCustomerId}
            submitLabel="Subscribe"
            onSuccess={() => {
              setSubscribeModalOpen(false);
              localStorage.removeItem("userInfo");
              window.location.reload();
            }}
          />
        </Elements>
      </Container>
    );
  }

  const { breakdown, total } = calculateTotalWithPaymentsPro(
    slots,
    paymentsPro
  );
  const currentPlanTotal = calculateTotalWithPaymentsPro(
    sub?.slots || 0,
    !!sub?.paymentsPro
  ).total;
  const hasChanges =
    sub?.hasSubscription &&
    (slots !== (sub.slots || 0) || paymentsPro !== !!sub.paymentsPro);

  const handleUpdate = async () => {
    if (!sub?.subscriptionId) return;
    setError("");
    setUpdating(true);
    try {
      await updateCoachSubscription(sub.subscriptionId, { slots, paymentsPro });
      const res = await getCoachSubscription();
      setSub(res?.hasSubscription ? res : sub);
    } catch (err) {
      setError(err?.message || "Failed to update subscription");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = async () => {
    if (!sub?.subscriptionId) return;
    setCancelling(true);
    try {
      await cancelCoachSubscription(sub.subscriptionId, cancelAtPeriodEnd);
      setCancelDialogOpen(false);
      const res = await getCoachSubscription();
      setSub(res);
    } catch (err) {
      setError(err?.message || "Failed to cancel");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Subscription
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {sub?.hasSubscription ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Current plan
            </Typography>
            <Typography variant="body1">
              <strong>{sub.slots || 0}</strong> client slots
              {sub.paymentsPro && " • Payments Pro ($10/mo)"}
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              <strong>${currentPlanTotal}/month</strong>
            </Typography>
            {sub.currentPeriodEnd && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Current period ends:{" "}
                {moment.unix(sub.currentPeriodEnd).format("MMM D, YYYY")}
              </Typography>
            )}
            {sub.cancelAtPeriodEnd && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Your subscription will cancel at the end of the billing period.
              </Alert>
            )}
          </CardContent>
        </Card>
      ) : null}
      {sub?.hasSubscription && !sub?.cancelAtPeriodEnd ? (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Update subscription
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upgrade and you&apos;ll be prorated. Downgrades take effect at the
              next billing cycle.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                Client slots: <strong>{slots}</strong>
              </Typography>
              <Slider
                value={slots}
                onChange={(_, v) => setSlots(v)}
                min={1}
                max={100}
                valueLabelDisplay="auto"
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={paymentsPro}
                  onChange={(e) => setPaymentsPro(e.target.checked)}
                />
              }
              label="Stripe / Payments Pro Add-On ($10/month)"
            />
            <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1, mt: 2 }}>
              <Typography variant="body2">
                New total: <strong>${total}/month</strong>
              </Typography>
            </Box>
            <LoadingButton
              variant="contained"
              sx={{ mt: 2 }}
              onClick={handleUpdate}
              loading={updating}
              disabled={!hasChanges}
            >
              Update
            </LoadingButton>
          </CardContent>
        </Card>
      ) : null}
      {sub?.hasSubscription && !sub?.cancelAtPeriodEnd && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Cancel subscription
            </Typography>
            <Button
              color="error"
              variant="outlined"
              onClick={() => setCancelDialogOpen(true)}
            >
              Cancel subscription
            </Button>
          </CardContent>
        </Card>
      )}
      {stripeCustomerId && (
        <Elements
          stripe={stripePromise}
          options={{
            appearance,
            fonts: [
              {
                family: "Roboto Mono, monospace",
                cssSrc:
                  "https://fonts.googleapis.com/css2?family=Roboto+Mono:wght@100;400;500;700",
              },
            ],
          }}
        >
          <CoachPaymentMethodsManager
            paymentMethods={paymentMethods}
            defaultPaymentMethodId={defaultPaymentMethodId}
            setDefaultPaymentMethodId={setDefaultPaymentMethodId}
            setPaymentMethods={setPaymentMethods}
            stripeCustomerId={stripeCustomerId}
            userEmail={user?.email || email}
            setDefaultPaymentMethod={setDefaultPaymentMethod}
            deletePaymentMethod={deletePaymentMethod}
          />
        </Elements>
      )}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
      >
        <DialogTitle>Cancel subscription</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={cancelAtPeriodEnd}
                onChange={(e) => setCancelAtPeriodEnd(e.target.checked)}
              />
            }
            label="Cancel at end of billing period (recommended)"
          />
          {!cancelAtPeriodEnd && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Immediate cancellation will end your access now. You won&apos;t be
              charged further.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Back</Button>
          <LoadingButton
            color="error"
            variant="contained"
            onClick={handleCancel}
            loading={cancelling}
          >
            Cancel subscription
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CoachSubscription;
